import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import FullscreenError from '@/components/utils/FullscreenError';
import { faBook, faSpinner, faArrowLeft, faSearch, faXmark, faVideo, faGraduationCap } from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { Module, Course, CourseID } from '@/models';

const PAGE_SIZE = 50;

export default function AdminAllModulesList() {
  const navigate = useNavigate();
  const [modules, setModules] = useState<Module[]>([]);
  const [courseMap, setCourseMap] = useState<Map<CourseID, Course>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [criticalError, setCriticalError] = useState<boolean>(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [publishedFilter, setPublishedFilter] = useState<boolean | null>(null);

  // Load courses for dropdown
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const response = await APIRoutes.listCourses({ limit: 1000 });
        const map = new Map<CourseID, Course>();
        for (const course of response.courses) {
          map.set(course.id, course);
        }
        setCourseMap(map);
      } catch (err) {
        console.error('Error loading courses for filter:', err);
      }
    };
    loadCourses();
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, publishedFilter]);

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const cursorToken = currentPage > 1 ? `$${currentPage}` : undefined;

      const response = await APIRoutes.listAllModules({
        limit: PAGE_SIZE,
        next: cursorToken,
        search: (debouncedSearchTerm.trim() === '') ? undefined : debouncedSearchTerm,
        published: publishedFilter ?? undefined,
      });

      setModules(response.modules || []);
      setTotal(response.total || 0);
    } catch (err) {
      console.error('Critical error loading modules list:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Module');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchTerm, publishedFilter]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const clearAllFilters = () => {
    setSearchTerm('');
    setPublishedFilter(null);
  };

  const hasActiveFilters = searchTerm !== '' || publishedFilter !== null;

  if (criticalError) {
    return (
      <FullscreenError
        title="Modulliste nicht verfügbar"
        message={error || 'Die Modulliste konnte nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchModules}
        onBack={() => navigate('/admin')}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link
            to="/admin"
            className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
          </Link>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faBook} className="w-8 h-8 text-indigo-600 dark:text-indigo-400 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Alle Module ({total})
            </h1>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col gap-4">
          {/* Search Bar */}
          <div className="flex-1">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              />
              <input
                type="text"
                placeholder="Suche nach Titel oder Beschreibung..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {searchTerm !== '' && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
                >
                  <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-4">

            {/* Published Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setPublishedFilter(publishedFilter === true ? null : true)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                    publishedFilter === true
                      ? 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/50 dark:text-green-400 dark:border-green-700'
                      : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                  }`}
                >
                  Veröffentlicht
                </button>
                <button
                  onClick={() => setPublishedFilter(publishedFilter === false ? null : false)}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors cursor-pointer ${
                    publishedFilter === false
                      ? 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/50 dark:text-yellow-400 dark:border-yellow-700'
                      : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                  }`}
                >
                  Entwurf
                </button>
              </div>
            </div>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div className="flex justify-end">
              <button
                onClick={clearAllFilters}
                className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline cursor-pointer"
              >
                Alle Filter zurücksetzen
              </button>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      )}

      {error && !criticalError && (
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {modules.length === 0 && !loading && !error && (
        <div className="text-center py-12">
          <FontAwesomeIcon icon={faBook} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {hasActiveFilters ? 'Keine Module gefunden, die den Filterkriterien entsprechen' : 'Keine Module gefunden'}
          </p>
        </div>
      )}

      {modules.length > 0 && !loading && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Titel
                  </th>
                  <th className="w-0 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Kurs
                  </th>
                  <th className="w-0 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="w-0 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Videos
                  </th>
                  <th className="w-0 px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Erstellt
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {modules.map((module) => {
                  const course = courseMap.get(module.courseID);
                  return (
                    <tr key={module.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <Link to={`/admin/courses/${module.courseID}/modules/${module.id}`} className="block">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {module.title}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap w-80">
                        <Link to={`/admin/courses/${module.courseID}`} className="block">
                          <div className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                            <FontAwesomeIcon icon={faGraduationCap} className="w-4 h-4 mr-1" />
                            {course?.title || 'Unbekannter Kurs'}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/courses/${module.courseID}/modules/${module.id}`} className="block">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            module.published
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400'
                          }`}>
                            {module.published ? 'Veröffentlicht' : 'Entwurf'}
                          </span>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/courses/${module.courseID}/modules/${module.id}`} className="block">
                          <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                            <FontAwesomeIcon icon={faVideo} className="w-4 h-4 mr-1" />
                            {module.videoCount ?? 0}
                          </div>
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/admin/courses/${module.courseID}/modules/${module.id}`} className="block">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(module.created)}
                          </div>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PageBasedPagination
            currentPage={currentPage}
            totalCount={total}
            pageSize={PAGE_SIZE}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>
      )}
    </>
  );
}
